import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Avisos flotantes ("guardado", "no se pudo…") para toda la aplicacion.
 *
 * POR QUE EXISTE. Cada pantalla daba su respuesta a su manera -- un texto junto
 * al boton, un rotulo que cambia, o NADA (etiquetar una partida, borrarla,
 * guardar desde el meta). El resultado era que guardar algo dejaba la duda de
 * si paso: habia que buscar la confirmacion en un sitio distinto cada vez.
 * Ahora toda operacion de guardar/actualizar/borrar responde en el MISMO
 * sitio, con el mismo lenguaje.
 *
 * REGLAS
 *  - Exito: se va solo a los 3.5 s. Confirma y no estorba.
 *  - Error: dura el doble Y lleva boton de cerrar. Un error que desaparece
 *    antes de leerse es como no avisar.
 *  - Se apilan (lo ultimo abajo) y NUNCA tapan la navegacion inferior movil.
 *
 * Uso: const { avisar } = useAvisos();  avisar('ok', t('matchSaved'));
 */

const AvisosContext = createContext(null);

const DURACION = { ok: 3500, warn: 5000, error: 7000 };

export function AvisosProvider({ children }) {
  const [lista, setLista] = useState([]);
  const siguiente = useRef(1);

  const cerrar = useCallback((id) => {
    // Primero se marca para que corra la animacion de salida; se desmonta al
    // terminar (ver onAnimationEnd en el render).
    setLista((prev) => prev.map((a) => (a.id === id ? { ...a, saliendo: true } : a)));
  }, []);

  const avisar = useCallback(
    (tipo, texto) => {
      const id = siguiente.current++;
      setLista((prev) => [...prev, { id, tipo, texto }]);
      const t = setTimeout(() => cerrar(id), DURACION[tipo] ?? DURACION.ok);
      return () => {
        clearTimeout(t);
        cerrar(id);
      };
    },
    [cerrar]
  );

  const value = useMemo(() => ({ avisar }), [avisar]);

  return (
    <AvisosContext.Provider value={value}>
      {children}
      {lista.length > 0 &&
        createPortal(
          <div className="avisos" role="status" aria-live="polite">
            {lista.map((a) => (
              <div
                key={a.id}
                className={`avisos__uno is-${a.tipo}${a.saliendo ? ' is-saliendo' : ''}`}
                onAnimationEnd={(e) => {
                  if (e.animationName === 'avisoSale') {
                    setLista((prev) => prev.filter((x) => x.id !== a.id));
                  }
                }}
              >
                <span className="avisos__icono" aria-hidden="true">
                  {a.tipo === 'ok' ? '✓' : a.tipo === 'warn' ? '⚠' : '✕'}
                </span>
                <span className="avisos__texto">{a.texto}</span>
                {/* Los errores se cierran a mano ademas de solos: leer con calma
                    un fallo importa mas que la limpieza de la pantalla. */}
                {a.tipo !== 'ok' && (
                  <button type="button" className="avisos__cerrar" onClick={() => cerrar(a.id)} aria-label="×">
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </AvisosContext.Provider>
  );
}

export function useAvisos() {
  const ctx = useContext(AvisosContext);
  if (!ctx) throw new Error('useAvisos debe usarse dentro de <AvisosProvider>');
  return ctx;
}
